/**
 * StatusChipsBar.jsx
 * Barra horizontal de chips para filtro rapido por status
 *
 * Chips: Moving | Parado | Offline | Ocioso
 * Click = toggle filtro
 * Chip ativo = destaque visual
 */

import { memo, useCallback, useTransition } from 'react';
import {
 Box, Chip, IconButton, Tooltip 
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FilterAltOff as ClearIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useDeviceStatusCounts } from '../../store/selectors';
import { radius, transitions } from '../../common/theme/premiumTokens';

const useStyles = makeStyles()((theme, { tokens }) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 6, // Reduzido para chips mais juntos
    padding: '6px 10px',
    overflowX: 'auto',
    overflowY: 'hidden',
    // Hide scrollbar but keep functionality
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    // Subtle top border - transparent para deixar glassmorphism do sidebar aparecer
    borderTop: `1px solid ${tokens.border.subtle}`,
    backgroundColor: 'transparent',
  },
  chip: {
    height: 22,
    borderRadius: radius.full,
    fontSize: '0.68rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: transitions.all,
    border: '1px solid transparent',
    '& .MuiChip-icon': {
      fontSize: 10,
      marginLeft: 6,
      marginRight: -2, // Dot mais perto do texto
    },
    '& .MuiChip-label': {
      paddingLeft: 4,
      paddingRight: 6,
    },
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  chipInactive: {
    backgroundColor: 'transparent',
    color: tokens.text.muted,
    borderColor: tokens.border.subtle,
    opacity: 0.7,
    '&:hover': {
      opacity: 1,
      backgroundColor: tokens.surface.glassHover,
      borderColor: tokens.border.default,
    },
  },
  // Status dot inside chip - menor e mais junto ao texto
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    marginRight: 2,
    flexShrink: 0,
  },
  // Clear button - sempre visível, alinhado à direita
  clearButton: {
    padding: 3,
    marginLeft: 2,
    color: tokens.text.muted,
    borderRadius: radius.md,
    transition: transitions.all,
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
      color: tokens.status.alarm,
      backgroundColor: alpha(tokens.status.alarm, 0.12),
    },
  },
  clearButtonActive: {
    opacity: 1,
    color: tokens.status.alarm,
  },
}));

// Status configuration com 4 estados visuais
// Moving=Azul, Online=Verde (parados), Offline=Vermelho, Ocioso=Cinza
const getStatusConfig = (tokens) => [
  {
    key: 'moving',
    label: 'Movimento',
    color: tokens.status.moving,
    filterType: 'motionStates',
    filterValue: 'moving',
  },
  {
    key: 'online',
    label: 'Online',
    color: tokens.status.online,
    filterType: 'motionStates',
    filterValue: 'stopped',
  },
  {
    key: 'offline',
    label: 'Offline',
    color: tokens.status.offline,
    filterType: 'statuses',
    filterValue: 'offline',
  },
  {
    key: 'unknown',
    label: 'Ocioso',
    color: tokens.status.unknown,
    filterType: 'statuses',
    filterValue: 'unknown',
  },
];

const StatusChipsBar = memo(({ filter, setFilter }) => {
  const tokens = usePremiumTokens();
  const { classes, cx } = useStyles({ tokens });
  const t = useTranslation();
  const [isPending, startTransition] = useTransition();

  // Get status counts from memoized selector
  const statusCounts = useDeviceStatusCounts();

  // Status config
  const statusConfig = getStatusConfig(tokens);

  // Check if any status filter is active
  const hasActiveFilters =
    (filter?.statuses?.length || 0) > 0 || (filter?.motionStates?.length || 0) > 0;

  // Check if specific status is selected
  const isStatusSelected = useCallback(
    (config) => {
      if (config.filterType === 'motionStates') {
        return (filter?.motionStates || []).includes(config.filterValue);
      }
      return (filter?.statuses || []).includes(config.filterValue);
    },
    [filter]
  );

  // Handle chip click - toggle filter
  const handleChipClick = useCallback(
    (config) => {
      if (!setFilter) return;

      startTransition(() => {
        if (config.filterType === 'motionStates') {
          const current = filter?.motionStates || [];
          const newValue = current.includes(config.filterValue)
            ? current.filter((s) => s !== config.filterValue)
            : [...current, config.filterValue];
          setFilter({ ...filter, motionStates: newValue });
        } else {
          const current = filter?.statuses || [];
          const newValue = current.includes(config.filterValue)
            ? current.filter((s) => s !== config.filterValue)
            : [...current, config.filterValue];
          setFilter({ ...filter, statuses: newValue });
        }
      });
    },
    [filter, setFilter]
  );

  // Clear all status filters
  const handleClearFilters = useCallback(() => {
    if (!setFilter) return;
    startTransition(() => {
      setFilter({
        ...filter,
        statuses: [],
        motionStates: [],
      });
    });
  }, [filter, setFilter]);

  // Get count for status
  // Nota: chip "online" mostra contagem de stopped + online (todos os online parados)
  const getCount = (key) => {
    switch (key) {
      case 'moving':
        return statusCounts.moving;
      case 'online':
        return (statusCounts.stopped || 0) + (statusCounts.online || 0);
      case 'offline':
        return statusCounts.offline;
      case 'unknown':
        return statusCounts.unknown;
      default:
        return 0;
    }
  };

  return (
    <Box className={classes.container}>
      {statusConfig.map((config) => {
        const isActive = isStatusSelected(config);
        const count = getCount(config.key);

        return (
          <Tooltip
            key={config.key}
            title={`Filtrar: ${config.label} (${count})`}
            arrow
            placement="bottom"
          >
            <Chip
              size="small"
              icon={
                <Box
                  className={classes.statusDot}
                  sx={{
                    backgroundColor: config.color,
                    boxShadow: isActive ? `0 0 6px ${config.color}` : 'none',
                  }}
                />
              }
              label={`${count} ${config.label}`}
              onClick={() => handleChipClick(config)}
              className={cx(classes.chip, !isActive && classes.chipInactive)}
              sx={
                isActive
                  ? {
                      backgroundColor: alpha(config.color, 0.15),
                      color: config.color,
                      borderColor: alpha(config.color, 0.4),
                      '&:hover': {
                        backgroundColor: alpha(config.color, 0.25),
                      },
                    }
                  : undefined
              }
            />
          </Tooltip>
        );
      })}

      {/* Clear button - sempre visível ao lado de Ocioso */}
      <Tooltip title={hasActiveFilters ? (t('filterClear') || 'Limpar filtros') : 'Sem filtros ativos'} arrow>
        <span>
          <IconButton
            size="small"
            className={cx(classes.clearButton, hasActiveFilters && classes.clearButtonActive)}
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
});

StatusChipsBar.displayName = 'StatusChipsBar';
export default StatusChipsBar;
